import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * AssetsList — Hardware Inventory list page
 *
 * Features:
 *   - 4 stat cards (Total, In Stock, Deployed, RMA)
 *   - Server-side DataGrid: pagination, sort, search, 3 filters
 *   - Ownership state filter (3rd dropdown — was missing)
 *   - Warranty expiry warning badge on rows
 *   - Row action menu (View / Transfer / Delete)
 *   - Empty state
 *   - Register Asset modal (single + bulk CSV)
 *   - Success / error alerts with auto-dismiss
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  Chip,
  Alert,
  Skeleton,
  Button,
  Stack,
  MenuItem,
  useTheme,
  InputAdornment,
  Collapse,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";

import { useNavigate } from "react-router-dom";
import axios from "axios";

// Icons
import AddIcon from "@mui/icons-material/Add";
import SensorsIcon from "@mui/icons-material/Sensors";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ClearIcon from "@mui/icons-material/Clear";
import PlaceIcon from "@mui/icons-material/Place";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import RegisterAssetModal from "./RegisterAssetModal";
import AssetTypeManagerModal from "./AssetTypeManagerModal";
import { useAuth } from "../../../../auth/useAuth";
import BulkTransferModal from "../Transfers/BulkTransferModal";
import BulkMovementModal from "../movements/BulkMovementModal";
import BulkProvisionModal from "../bindings/BulkProvisionModal";
import CategoryIcon from "@mui/icons-material/Category";
import { useNotification } from "../../../../context/NotificationContext";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

// ─── Status colour map ────────────────────────────────────────────────────────

const STATUS_COLORS = {
  IN_STOCK: { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
  DEPLOYED: { color: "#15803d", bg: "#dcfce7", border: "#bbf7d0" },
  DAMAGED: { color: "#b91c1c", bg: "#fee2e2", border: "#fecaca" },
  LOST: { color: "#a16207", bg: "#fef08a", border: "#fde047" },
};
const statusStyle = (s) => STATUS_COLORS[s] || STATUS_COLORS.IN_STOCK;

// ─── Row action menu ──────────────────────────────────────────────────────────

const normalize = (val) =>
  (val || "").toString().toLowerCase().replace(/_/g, " ").trim();

const getDeleteAssetErrorMessage = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  "Delete failed";

function RowActions({
  row,
  token,
  activeOrg,
  onDeleted,
  navigate,
  canTransfer,
  canDelete,
  transferLabel,
}) {
  const [anchor, setAnchor] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const theme = useTheme();
  const open = Boolean(anchor);

  const handleDelete = async () => {
  setDeleting(true);
  try {
    await axios.delete(`${API_BASE}/assets/${row.id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
      },
    });

    onDeleted(row.id, row.serial_number);

    setOpenDeleteDialog(false);   // ✅ close dialog
  } catch (err) {
    alert(getDeleteAssetErrorMessage(err));
    setDeleting(false);
  }
};

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        disabled={deleting}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            minWidth: 170,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/inventory/assets/${row.id}`);
            setAnchor(null);
          }}
          sx={{ fontSize: 13 }}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View detail</ListItemText>
        </MenuItem>
        {canTransfer && (
          <MenuItem
            onClick={() => {
              navigate(`/inventory/assets/${row.id}?action=transfer`);
              setAnchor(null);
            }}
            disabled={
              row.ownership_state === "RMA_RETURNED" ||
              row.status === "DEPLOYED"
            }
            sx={{ fontSize: 13 }}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{transferLabel}</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => setOpenDeleteDialog(true)}
            disabled={deleting}
            sx={{ fontSize: 13, color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              {deleting ? "Deleting…" : "Delete asset"}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Dialog
  open={openDeleteDialog}
  onClose={() => setOpenDeleteDialog(false)}
>
  <DialogTitle>Delete Asset</DialogTitle>

  <DialogContent>
    <DialogContentText>
      Are you sure you want to delete asset <b>{row.serial_number}</b>?
    </DialogContentText>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setOpenDeleteDialog(false)}>
      Cancel
    </Button>

    <Button
      color="error"
      variant="contained"
      onClick={handleDelete}
      disabled={deleting}
    >
      {deleting ? "Deleting..." : "Delete"}
    </Button>
  </DialogActions>
</Dialog>
    </>
  );
}

// ─── Warranty badge ───────────────────────────────────────────────────────────

function WarrantyBadge({ warrantyEnd }) {
  if (!warrantyEnd) return null;
  const today = new Date();
  const endDate = new Date(warrantyEnd);
  const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  if (daysLeft > 90) return null;
  const expired = daysLeft < 0;
  return (
    <Tooltip
      title={
        expired ? "Warranty expired" : `Warranty expires in ${daysLeft} days`
      }
    >
      <WarningAmberIcon
        fontSize="small"
        sx={{
          color: expired ? "error.main" : "warning.main",
          verticalAlign: "middle",
          ml: 0.5,
        }}
      />
    </Tooltip>
  );
}

// ─── Custom toolbar ───────────────────────────────────────────────────────────

function CustomToolbar({ searchQuery, onSearch }) {
  return (
    <GridToolbarContainer
      sx={{
        p: 1.5,
        justifyContent: "space-between",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "hardware_inventory" }} />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: 260 } }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search serial, model, IMEI…"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: { backgroundColor: "background.default", borderRadius: 1 },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function AssetsList() {
  const {
    notification,
    showNotification,
    closeNotification,
  } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  //const { showNotification } = useNotification();
  const token = localStorage.getItem("authToken");
  const isPlatformOwner =
    permissions?.has?.("*") || hasSystemManage(permissions);
  const orgType = (activeOrg?.type || "").toUpperCase();
  const isDistributor = orgType === "DISTRIBUTOR";
  const isReseller = orgType === "RESELLER";
  const isCustomer = orgType === "CUSTOMER";

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assets, setAssets] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([
    { field: "created_at", sort: "desc" },
  ]);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerTypeFilter, setOwnerTypeFilter] = useState("");
  const [ownershipStateFilter, setOwnershipStateFilter] = useState(""); // NEW
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // UI state
  const [modalOpen, setModalOpen] = useState(false);
  const [assetTypeManagerOpen, setAssetTypeManagerOpen] = useState(false);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkMovementOpen, setBulkMovementOpen] = useState(false);
  const [bulkAssignLocationOpen, setBulkAssignLocationOpen] = useState(false);
  const [bulkProvisionOpen, setBulkProvisionOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });
  // const [successAlert, setSuccessAlert] = useState("");
  const [searchText, setSearchText] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    inStock: 0,
    deployed: 0,
    rma: 0,
    loading: true,
  });
  const [assetTypes, setAssetTypes] = useState([]);
  const latestFetchIdRef = useRef(0);

  // ── Helpers ──
  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  function hasSystemManage(perms) {
    if (!perms || perms.size === 0) return false;
    return perms.has("*") || perms.has("system.manage");
  }

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // const showSuccess = (msg) => {
  //   setSuccessAlert(msg);
  //   setTimeout(() => setSuccessAlert(""), 5000);
  // };

  const loadAssetTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/asset-types`, {
        headers: authHeaders,
      });
      setAssetTypes(res.data?.data || []);
    } catch {
      setAssetTypes([]);
    }
  }, [token, activeOrg, permissions]);

  const selectedIds = Array.from(rowSelectionModel?.ids || []);
  const selectedAssets = assets.filter((asset) =>
    (rowSelectionModel?.ids || new Set()).has(asset.id),
  );
  const selectedInStockAssets = selectedAssets.filter(
    (asset) => asset.status === "IN_STOCK",
  );

  // ── Fetch assets ──
  const fetchAssets = useCallback(async () => {
    if (!hasPermission("inventory.asset.view")) return;
    const fetchId = latestFetchIdRef.current + 1;
    latestFetchIdRef.current = fetchId;
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        sort: sortModel[0]?.field || "created_at",
        order: sortModel[0]?.sort || "desc",
        ...(searchQuery && { q: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(ownerTypeFilter && { owner_type: ownerTypeFilter }),
        ...(ownershipStateFilter && { ownership_state: ownershipStateFilter }),
        ...(assetTypeFilter && { asset_type_id: assetTypeFilter }),
      };
      const res = await axios.get(`${API_BASE}/assets`, {
        headers: authHeaders,
        params,
      });
      if (latestFetchIdRef.current !== fetchId) return;
      setAssets(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch (err) {
      if (latestFetchIdRef.current !== fetchId) return;
      console.error(err);
      setError(
        "Failed to load inventory data. Check your connection and try again.",
      );
    } finally {
      if (latestFetchIdRef.current !== fetchId) return;
      setLoading(false);
    }
  }, [
    token,
    activeOrg,
    paginationModel,
    sortModel,
    searchQuery,
    statusFilter,
    ownerTypeFilter,
    ownershipStateFilter,
    assetTypeFilter,
  ]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    loadAssetTypes();
  }, [loadAssetTypes]);

  // ── Fetch stats (4 parallel requests) ──
  const fetchStats = useCallback(() => {
    if (!hasPermission("inventory.asset.view")) return;
    setStats((prev) => ({ ...prev, loading: true }));
    Promise.all([
      axios.get(`${API_BASE}/assets`, {
        headers: authHeaders,
        params: { per_page: 1 },
      }),
      axios.get(`${API_BASE}/assets`, {
        headers: authHeaders,
        params: { per_page: 1, status: "IN_STOCK" },
      }),
      axios.get(`${API_BASE}/assets`, {
        headers: authHeaders,
        params: { per_page: 1, status: "DEPLOYED" },
      }),
      axios.get(`${API_BASE}/assets`, {
        headers: authHeaders,
        params: { per_page: 1, ownership_state: "RMA_RETURNED" },
      }),
    ])
      .then(([t, s, d, r]) =>
        setStats({
          total: t.data.meta?.pagination?.total_items ?? 0,
          inStock: s.data.meta?.pagination?.total_items ?? 0,
          deployed: d.data.meta?.pagination?.total_items ?? 0,
          rma: r.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ── Handlers ──
  const handleSearch = (val) => {
    setSearchQuery(val);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRegisterSuccess = ({ type, asset, created, failed }) => {
    fetchAssets();
    fetchStats();
    setModalOpen(false);
    showNotification(
      type === "single"
        ? `Asset "${asset?.serial_number}" registered successfully.`
        : `Bulk upload complete — ${created} asset${created !== 1 ? "s" : ""} created${failed > 0 ? `, ${failed} failed` : ""}.`,
      "success"
    );
  };

  const handleDeleted = (id, sn) => {
    setAssets((prev) => prev.filter((a) => a.id !== id));
    setRowCount((prev) => prev - 1);
    fetchStats();
    showNotification(`Asset "${sn}" deleted.`, "success");
  };

  const handleBulkTransferSuccess = (payload) => {
    const { succeeded = 0, failed = 0 } = payload || {};
    fetchAssets();
    fetchStats();
    setRowSelectionModel({ type: "include", ids: new Set() });
    showNotification(
      `Bulk transfer complete — ${succeeded} succeeded${failed > 0 ? `, ${failed} failed` : ""}.`, "success"
    );
  };

  const handleBulkMovementSuccess = (payload, successCopy) => {
    const { succeeded = 0, failed = 0 } = payload || {};
    fetchAssets();
    showNotification(
      `${successCopy} — ${succeeded} succeeded${failed > 0 ? `, ${failed} failed` : ""}.`, "success"
    );
  };

  const handleBulkProvisionSuccess = (payload) => {
    const { succeeded = 0, failed = 0 } = payload || {};
    fetchAssets();
    fetchStats();
    setRowSelectionModel({ type: "include", ids: new Set() });
    showNotification(
      `Bulk provisioning complete — ${succeeded} succeeded${failed > 0 ? `, ${failed} failed` : ""}.`, "success"
    );
  };

  // ── Columns ──
  const columns = [
    {
      field: "serialIndex",
      headerName: "S.No",
      width: 64,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {paginationModel.page * paginationModel.pageSize +
              params.api.getRowIndexRelativeToVisibleRows(params.id) +
              1}
          </Typography>
        </Box>
      ),
    },
    {
      field: "serial_number",
      headerName: "Serial Number",
      flex: 1.3,
      minWidth: 140,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5} sx={{ py: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "primary.main",
              "&:hover": { textDecoration: "underline" },
              fontFamily: "monospace",
              fontSize: 12,
            }}
          >
            {params.value}
          </Typography>
          <WarrantyBadge warrantyEnd={params.row.warranty_end} />
        </Box>
      ),
    },
    { field: "asset_type", headerName: "Asset Type", flex: 1, minWidth: 120 },
    { field: "model", headerName: "Model", flex: 1, minWidth: 110 },
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        const s = statusStyle(params.value);
        return (
          <Chip
            label={params.value?.replace(/_/g, " ")}
            size="small"
            sx={{
              bgcolor: s.bg,
              color: s.color,
              fontWeight: 600,
              border: `1px solid ${s.border}`,
              borderRadius: "6px",
              fontSize: 11,
            }}
          />
        );
      },
    },
    {
      field: "ownership_state",
      headerName: "Ownership",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: 12, py: 2 }}
        >
          {params.value?.replace(/_/g, " ")}
        </Typography>
      ),
    },
    { field: "owner_type", headerName: "Owner Type", flex: 0.8, minWidth: 100 },
    {
      field: "_actions",
      headerName: "",
      width: 48,
      sortable: false,
      renderCell: (params) => (
        <RowActions
          row={params.row}
          token={token}
          activeOrg={activeOrg}
          onDeleted={handleDeleted}
          navigate={navigate}
          canTransfer={hasPermission("inventory.transfer.create")}
          canDelete={isPlatformOwner && hasPermission("inventory.asset.delete")}
          transferLabel={
            isPlatformOwner
              ? "Transfer ownership"
              : isDistributor || isReseller
                ? "Sell / transfer onward"
                : "Transfer asset"
          }
        />
      ),
    },
  ];

  // ── Guard ──
  if (!hasPermission("inventory.asset.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view inventory assets.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: "Total Assets",
      value: stats.total,
      icon: <SensorsIcon />,
      bg: "#d3e9fa",
      color: "#1976d2",
    },
    {
      title: "In Stock",
      value: stats.inStock,
      icon: <InventoryIcon color="action" />,
      bg: "#f1f5f9",
    },
    {
      title: "Active/Deployed",
      value: stats.deployed,
      icon: <CheckCircleOutlineIcon color="success" />,
      bg: "#dcfce7",
    },
    {
      title: isPlatformOwner ? "RMA Returns" : "Service Returns",
      value: stats.rma,
      icon: <AssignmentReturnIcon color="warning" />,
      bg: "#fff7ed",
    },
  ];

  const hasActiveFilters =
    statusFilter ||
    ownerTypeFilter ||
    ownershipStateFilter ||
    assetTypeFilter ||
    searchQuery;

  const filteredAssets = assets.filter((row) => {
    const query = normalize(searchText);

    return (
      normalize(row.serial_number).includes(query) ||
      normalize(row.asset_type).includes(query) ||
      normalize(row.model).includes(query) ||
      normalize(row.manufacturer).includes(query) ||
      normalize(row.status).includes(query) ||
      normalize(row.ownership_state).includes(query) ||
      normalize(row.owner_type).includes(query)
    );
  });
  return (
    <Box sx={{ maxWidth: "1400px", margin: "0 auto", p: { xs: 2, md: 3 } }}>
      {/* ── Header ── */}

      {/* ── Alerts ── */}
      <Collapse in={notification.open}>
  <Alert
    severity={notification.severity}
    sx={{ mb: 3, borderRadius: 2 }}
    onClose={closeNotification}
  >
    {notification.message}
  </Alert>
</Collapse>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {!isPlatformOwner && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {isDistributor
            ? "Distributor workspace: view stock in your custody, sell in-stock assets onward, and provision only from in-stock unbound hardware."
            : isReseller
              ? "Reseller workspace: track received stock, transfer it to customers, and provision only from eligible in-stock assets."
              : "Org workspace: inventory is limited to assets owned by or assigned to your workspace."}
        </Alert>
      )}

      {/* ── Stat Cards ── */}
      {selectedIds.length > 0 && (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {selectedIds.length} asset{selectedIds.length !== 1 ? "s" : ""}{" "}
                selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Keep single-asset actions for one-off decisions, or use this
                batch bar for downstream warehouse operations.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {hasPermission("inventory.transfer.create") && (
                <Button
                  variant="contained"
                  startIcon={<MoveUpIcon />}
                  onClick={() => setBulkTransferOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Bulk transfer
                </Button>
              )}
              {hasPermission("inventory.movement.create") && (
                <Button
                  variant="outlined"
                  startIcon={<MoveUpIcon />}
                  onClick={() => setBulkMovementOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Bulk movement
                </Button>
              )}
              {hasPermission("inventory.movement.create") && (
                <Button
                  variant="outlined"
                  startIcon={<PlaceIcon />}
                  onClick={() => setBulkAssignLocationOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Assign location
                </Button>
              )}
              {hasPermission("device.create") && (
                <Button
                  variant="outlined"
                  startIcon={<DeviceHubIcon />}
                  onClick={() => setBulkProvisionOpen(true)}
                  disabled={!selectedInStockAssets.length}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Bulk provision
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() =>
                  setRowSelectionModel({ type: "include", ids: new Set() })
                }
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Clear selection
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 4,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          borderRadius: 5,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        {/* ── Header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={6}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
              Hardware Inventory
            </Typography>
            <Typography variant="body2" color="#fff">
              Manage and track lifecycle of deployed devices.
            </Typography>
          </Box>
          {hasPermission("inventory.asset.create") && (
            <Stack
              direction={{ xs: "column", sm: "row" }} // 👈 column on mobile
              spacing={2}
              width={{ xs: "100%", sm: "auto" }}
            >
              <Button
                fullWidth // 👈 takes full width on mobile
                variant="outlined"
                startIcon={<CategoryIcon />}
                onClick={() => setAssetTypeManagerOpen(true)}
                sx={{
                  px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                       whiteSpace: "nowrap",
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
              >
                Manage Asset Types
              </Button>

              <Button
                fullWidth // 👈 same here
                variant="contained"
                startIcon={<AddIcon />}
                disableElevation
                onClick={() => setModalOpen(true)}
                sx={{
                 px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
              >
                Register Asset
              </Button>
            </Stack>
          )}
        </Stack>
        {/* ── Stat Cards ── */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr", // mobile → 1 card
            sm: "1fr 1fr", // tablet → 2 cards
            md: "1fr 1fr", // small laptop → 2
            lg: "1fr 1fr 1fr 1fr", // desktop → 4 cards in one row
          }}
          gap={2}
          mb={1}
        >
          {stats.loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 7,
                    p: 2,

                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",

                    color: "#fff",

                    transition: "all 0.25s ease",

                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
                    },
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{ mr: 2, borderRadius: 2 }}
                  />
                  <Box flex={1}>
                    <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={32} />
                  </Box>
                </Card>
              ))
            : statCards.map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    p: 2,

                    // 🔥 FIXED BACKGROUND (no more white)
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",

                    color: "#fff",

                    transition: "all 0.25s ease",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    {/* ICON */}
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        color: s.color,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {s.icon}
                    </Box>

                    {/* TEXT */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}
                      >
                        {s.title}
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{ color: "#fff", fontWeight: 700 }}
                      >
                        {s.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
        </Box>
      </Box>
      {/* ── Table card ── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: "hidden",

          // 🔥 glass theme
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(14px)",

          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Filters bar */}
        <Box
          p={2}
          borderBottom="1px solid"
          borderColor="divider"
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,

            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

            borderBottom: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {/* Status */}
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
           sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="IN_STOCK">In Stock</MenuItem>
            <MenuItem value="DEPLOYED">Deployed</MenuItem>
            <MenuItem value="DAMAGED">Damaged</MenuItem>
            <MenuItem value="LOST">Lost</MenuItem>
          </TextField>

          {/* Owner type */}
          <TextField
            select
            size="small"
            label="Owner type"
            value={ownerTypeFilter}
            onChange={handleFilterChange(setOwnerTypeFilter)}
             sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All owners</MenuItem>
            <MenuItem value="OEM">OEM</MenuItem>
            <MenuItem value="DISTRIBUTOR">Distributor</MenuItem>
            <MenuItem value="RESELLER">Reseller</MenuItem>
            <MenuItem value="CUSTOMER">Customer</MenuItem>
          </TextField>

          {/* Ownership state — NEW */}
          <TextField
            select
            size="small"
            label="Ownership state"
            value={ownershipStateFilter}
            onChange={handleFilterChange(setOwnershipStateFilter)}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All states</MenuItem>
            <MenuItem value="OEM_OWNED">OEM Owned</MenuItem>
            <MenuItem value="OWNED_BY_PARTNER">Owned by partner</MenuItem>
            <MenuItem value="OWNED_BY_CUSTOMER">Owned by customer</MenuItem>
            <MenuItem value="RMA_RETURNED">RMA Returned</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Asset type"
            value={assetTypeFilter}
            onChange={handleFilterChange(setAssetTypeFilter)}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All asset types</MenuItem>
            {assetTypes.map((assetType) => (
              <MenuItem key={assetType.id} value={assetType.id}>
                {assetType.name}
                {assetType.code ? ` (${assetType.code})` : ""}
              </MenuItem>
            ))}
          </TextField>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setStatusFilter("");
                setOwnerTypeFilter("");
                setOwnershipStateFilter("");
                setAssetTypeFilter("");
                setSearchQuery("");
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              sx={{
                textTransform: "none",
                fontSize: 12,
                height: 38,

                color: "#fff", // text color
                borderColor: "rgba(255,255,255,0.4)", // border

                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Clear filters
            </Button>
          )}

          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 360 },
              mr: "auto",
              borderRadius: 2,

              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{ color: "#fff" }}
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            height: assets.length === 0 && !loading ? 320 : 640,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredAssets}
            rowCount={rowCount}
            loading={loading}
            checkboxSelection
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[15, 20, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newSelection) =>
              setRowSelectionModel(newSelection)
            }
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(params) => {
              if (params.field === "_actions") return;
              navigate(`/inventory/assets/${params.row.id}`);
            }}
            slots={{
              toolbar: () => (
                <CustomToolbar
                  searchQuery={searchQuery}
                  onSearch={handleSearch}
                />
              ),
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <InventoryIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />

                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {hasActiveFilters
                      ? "No assets match these filters"
                      : "No assets registered yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {hasActiveFilters
                      ? "Try adjusting or clearing the filters above"
                      : isPlatformOwner
                        ? 'Click "Register Asset" to add the first one'
                        : "Transferred or registered stock will appear here when it enters your custody."}
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              backgroundColor: "#FFF",

              "& .MuiDataGrid-columnHeaders": {
                background:
                  theme.palette.mode === "dark"
                    ? "#1e293b"
                    : "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
                color: "#334155",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              },

              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#F8FAFC",
              },
            }}
          />
        </Box>
      </Card>

      {/* ── Register Modal ── */}
      <RegisterAssetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleRegisterSuccess}
        token={token}
        activeOrg={activeOrg}
      />
      <AssetTypeManagerModal
        open={assetTypeManagerOpen}
        onClose={() => setAssetTypeManagerOpen(false)}
        onSuccess={() => {
          loadAssetTypes();
          showNotification("Asset type catalog updated successfully.");
        }}
        token={token}
        activeOrg={activeOrg}
      />
      <BulkTransferModal
        open={bulkTransferOpen}
        onClose={() => setBulkTransferOpen(false)}
        onSuccess={handleBulkTransferSuccess}
        assets={selectedAssets}
        token={token}
        activeOrg={activeOrg}
      />
      <BulkMovementModal
        open={bulkMovementOpen}
        onClose={() => setBulkMovementOpen(false)}
        onSuccess={(payload) =>
          handleBulkMovementSuccess(payload, "Bulk movement logging completed")
        }
        assets={selectedAssets}
        token={token}
        activeOrg={activeOrg}
      />
      <BulkMovementModal
        open={bulkAssignLocationOpen}
        onClose={() => setBulkAssignLocationOpen(false)}
        onSuccess={(payload) =>
          handleBulkMovementSuccess(
            payload,
            "Bulk location assignment completed",
          )
        }
        assets={selectedAssets}
        token={token}
        activeOrg={activeOrg}
        mode="assignLocation"
      />
      <BulkProvisionModal
        open={bulkProvisionOpen}
        onClose={() => setBulkProvisionOpen(false)}
        onSuccess={handleBulkProvisionSuccess}
        assets={selectedInStockAssets}
        token={token}
        activeOrg={activeOrg}
      />
    </Box>
  );
}
